import { z } from "zod";

/** Entità a cui un documento può essere collegato (ogni modulo registra le sue). */
export const DOCUMENT_ENTITIES = ["company", "contact", "deal", "project", "task", "quote", "invoice", "supplier_invoice", "contract", "expense"] as const;
export type DocumentEntityType = (typeof DOCUMENT_ENTITIES)[number];

/** Provenienza del documento. */
export const DOCUMENT_SOURCES = ["drive", "note", "entity", "import"] as const;
export type DocumentSourceType = (typeof DOCUMENT_SOURCES)[number];

/** Dimensione massima del singolo file (25 MB). */
export const DOCUMENT_MAX_SIZE = 25 * 1024 * 1024;

/**
 * Estensioni rifiutate a prescindere dal MIME dichiarato dal browser:
 * eseguibili e script che non hanno motivo di stare in un archivio CRM.
 */
export const DOCUMENT_BLOCKED_EXTENSIONS = [
  ".exe", ".msi", ".bat", ".cmd", ".com", ".scr", ".pif", ".cpl",
  ".jar", ".js", ".mjs", ".vbs", ".vbe", ".ws", ".wsf", ".ps1",
  ".sh", ".app", ".dmg", ".deb", ".rpm", ".dll", ".so",
] as const;

const folderName = z
  .string()
  .trim()
  .min(1)
  .max(120)
  // niente separatori di percorso: il nome è un'etichetta, non un path
  .refine((v) => !/[\\/]/.test(v), { message: "invalid_folder_name" });

export const folderInputSchema = z.object({
  name: folderName,
  parentId: z.string().uuid().nullish(),
});

export const folderUpdateSchema = z
  .object({
    name: folderName.optional(),
    /** null = sposta in radice */
    parentId: z.string().uuid().nullable().optional(),
  })
  .refine((v) => v.name !== undefined || v.parentId !== undefined, {
    message: "empty_update",
  });

export const documentUpdateSchema = z
  .object({
    filename: z.string().trim().min(1).max(255).optional(),
    /** null = sposta in radice */
    folderId: z.string().uuid().nullable().optional(),
  })
  .refine((v) => v.filename !== undefined || v.folderId !== undefined, {
    message: "empty_update",
  });

export const documentListQuerySchema = z.object({
  /** id cartella, oppure "root" per il primo livello del Drive */
  folderId: z.string().optional(),
  entityType: z.enum(DOCUMENT_ENTITIES).optional(),
  entityId: z.string().uuid().optional(),
  noteId: z.string().uuid().optional(),
  /** ricerca full-name su tutto il tenant (ignora folderId) */
  q: z.string().trim().max(200).optional(),
});

/**
 * Campi accettati come parte non-file della multipart di upload.
 * Arrivano sempre come stringhe: i campi assenti vengono omessi dal client.
 */
export const documentUploadFieldsSchema = z.object({
  folderId: z.string().uuid().optional(),
  entityType: z.enum(DOCUMENT_ENTITIES).optional(),
  entityId: z.string().uuid().optional(),
  noteId: z.string().uuid().optional(),
});

export type FolderInput = z.infer<typeof folderInputSchema>;
export type DocumentUpdateInput = z.infer<typeof documentUpdateSchema>;

/** Estensione (con il punto) in minuscolo, "" se assente. */
export function fileExtension(filename: string): string {
  const i = filename.lastIndexOf(".");
  return i > 0 ? filename.slice(i).toLowerCase() : "";
}

export function isBlockedFilename(filename: string): boolean {
  return (DOCUMENT_BLOCKED_EXTENSIONS as readonly string[]).includes(fileExtension(filename));
}

/** Logo dello spazio di lavoro: immagini raster o SVG fino a 2 MB. */
export const LOGO_MAX_SIZE = 2 * 1024 * 1024;
export const LOGO_MIME_TYPES = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"] as const;

export const documentListSchema = z.union([
  z.object({ entityType: z.enum(DOCUMENT_ENTITIES), entityId: z.string().uuid() }),
  z.object({ folderId: z.string().uuid().nullable(), q: z.string().trim().max(120).optional() }),
]);
export type DocumentListInput = z.infer<typeof documentListSchema>;
